import { forwardRef } from 'react'

import { cn } from '@/lib/utils'

import { FxIcon } from './FxIcon'

/**
 * A Musterformular in the overview grid: the form's first page on a `fill`
 * frame, badges over it, and a white footer with the name in `cardName` and a
 * kebab. 284px wide, `radius-sm`, `shadow-raised`; selected gains the 2px
 * `primary` outline, which is one of the two borders the design system draws.
 *
 * Beyond the design system's contract this takes `href`, `onOpen`, `onMenu`
 * and `onToggleBookmark`: the artboards wrap the whole card in a link to the
 * cockpit, and a card with a kebab inside a link is not valid markup. So the
 * name is the link and it is stretched over the card by a pseudo-element,
 * which leaves the kebab and the bookmark as siblings rather than nested
 * interactive elements, and gives the keyboard one stop per action.
 */

/**
 * The stand-in page drawn when a template has no rendered thumbnail yet — a
 * sheet with the wordmark, a title bar and ruled lines. The line widths are
 * derived from the index so the same card always draws the same page.
 */
function FormThumb() {
  return (
    <div
      aria-hidden
      className="mt-[30px] flex aspect-[1/1.414] flex-col gap-[7px] overflow-hidden bg-surface px-[14px] py-[22px] shadow-raised"
    >
      <div className="mb-[6px] flex items-center gap-[3px] self-end text-[9px] text-primary">
        <b className="inline-block h-[10px] w-[10px] rounded-full bg-primary" />
        formalogix
      </div>
      <div className="mb-1 h-[6px] w-3/5 bg-text opacity-70" />
      {Array.from({ length: 14 }, (_, i) => (
        <i key={i} className="block h-[3px] bg-fill" style={{ width: `${55 + ((i * 37) % 40)}%` }} />
      ))}
    </div>
  )
}

function Badge({
  className,
  label,
  icon,
  filled,
  onClick,
}: {
  className: string
  label: string
  icon: string
  filled?: boolean
  onClick?: () => void
}) {
  const content = (
    <>
      <FxIcon name={icon} size={18} filled={filled} />
      <span className="sr-only">{label}</span>
    </>
  )
  const shape = cn(
    'inline-flex h-7 w-7 items-center justify-center rounded-sm text-on-primary',
    className,
  )
  // Without a handler the badge is a mark, not a control: the lock and the
  // check say what the template is, and only the bookmark is ever toggled.
  return onClick ? (
    <button type="button" className={cn(shape, 'relative z-10')} onClick={onClick} title={label}>
      {content}
    </button>
  ) : (
    <span className={shape} title={label}>
      {content}
    </span>
  )
}

export interface FxTemplateCardProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onSelect'> {
  name: string
  /** URL of the rendered first page; without one a neutral sheet is drawn. */
  thumbnail?: string
  locked?: boolean
  done?: boolean
  bookmarked?: boolean
  selected?: boolean
  href?: string
  onOpen?: () => void
  onMenu?: () => void
  onToggleBookmark?: () => void
}

export const FxTemplateCard = forwardRef<HTMLElement, FxTemplateCardProps>(
  (
    {
      name,
      thumbnail,
      locked,
      done,
      bookmarked,
      selected,
      href,
      onOpen,
      onMenu,
      onToggleBookmark,
      className,
      ...props
    },
    ref,
  ) => (
    <article
      ref={ref}
      className={cn(
        'relative box-border w-card-template max-w-full overflow-hidden rounded-sm border-2 border-transparent bg-surface shadow-raised',
        selected && 'border-primary',
        className,
      )}
      {...props}
    >
      <div className="relative bg-fill px-4 pb-4 pt-[14px]">
        <div className="absolute inset-x-[30px] top-[14px] z-[1] flex gap-2">
          {locked && <Badge className="bg-text-muted" label="Gesperrt" icon="lock" filled />}
          {done && <Badge className="bg-success" label="Analysiert" icon="check_circle" />}
          <span className="flex-1" />
          <Badge
            className="bg-primary"
            label={bookmarked ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
            icon="bookmark"
            filled={bookmarked}
            onClick={onToggleBookmark}
          />
        </div>
        {thumbnail ? (
          <img
            className="mt-[30px] block aspect-[1/1.414] w-full bg-surface object-cover shadow-raised"
            src={thumbnail}
            alt=""
          />
        ) : (
          <FormThumb />
        )}
      </div>
      <div className="flex h-list-row items-center gap-2 pl-4 pr-2">
        <CardName name={name} href={href} onOpen={onOpen} />
        {onMenu && (
          <button
            type="button"
            className="relative z-10 flex rounded-sm p-1 text-text"
            aria-label={`Aktionen für ${name}`}
            onClick={onMenu}
          >
            <FxIcon name="more_vert" size={24} />
          </button>
        )}
      </div>
    </article>
  ),
)
FxTemplateCard.displayName = 'FxTemplateCard'

// `after:absolute after:inset-0` makes the whole card the link's hit area
// without putting the kebab inside the anchor.
function CardName({ name, href, onOpen }: { name: string; href?: string; onOpen?: () => void }) {
  const text = 'min-w-0 flex-1 truncate text-left text-card-name text-text'
  const stretch = 'after:absolute after:inset-0 after:content-[""]'
  if (href) {
    return (
      <a href={href} className={cn(text, stretch, 'no-underline')} onClick={onOpen}>
        {name}
      </a>
    )
  }
  if (onOpen) {
    return (
      <button type="button" className={cn(text, stretch)} onClick={onOpen}>
        {name}
      </button>
    )
  }
  return <span className={text}>{name}</span>
}
